import { HousingComparisonData } from '../../types/housingComparison';

/**
 * Ready-made rent-vs-buy scenarios. Each one answers a specific question and cites where
 * its assumptions came from, so users can start from a credible baseline instead of a blank form.
 *
 * Loading a template = handleLoadScenario(template.data, template.name) in HousingCalculator.
 * Templates are filtered by the site's country toggle, so data.region must match the audience.
 */
export interface HousingTemplate {
  id: string;
  name: string;
  question: string;
  attribution: string;
  source: string;
  data: HousingComparisonData;
}

const CANADA_BASE: HousingComparisonData = {
  region: 'Canada',
  analysisMode: 'compare',
  isLandlord: false,
  ownershipYears: 25,
  houseCost: 512300,
  closingCostsPercent: 2,
  downpaymentAmount: 102460,
  downpaymentPercent: 20,
  useDownpaymentPercent: true,
  mortgageYears: 25,
  interestRate: 4.5,
  propertyTaxPercent: 0.7,
  maintenancePercent: 1.3,
  condoFees: 0,
  annualAppreciation: 3,
  sellingCostsPercent: 5,
  taxesPercent: 0,
  monthlyRent: 2119,
  propertyManagementFeePercent: 8,
  vacancyRatePercent: 5,
  investmentReturnRate: 6,
  rentalInflationRate: 3,
  investmentTaxRate: 0,
  marginalTaxRate: 40,
  inflationRate: 3,
  extraMonthlyPayment: 0,
  annualHomeInsurance: 600
};

const US_BASE: HousingComparisonData = {
  ...CANADA_BASE,
  region: 'US',
  marginalTaxRate: 22,
  ownershipYears: 10,
  houseCost: 400000,
  closingCostsPercent: 3,
  downpaymentAmount: 40000,
  downpaymentPercent: 10,
  mortgageYears: 30,
  interestRate: 6.5,
  propertyTaxPercent: 1.1,
  maintenancePercent: 1,
  sellingCostsPercent: 6,
  monthlyRent: 2200,
  annualHomeInsurance: 1500
};

const BEN_FELIX = 'Ben Felix, PWL Capital';

export const HOUSING_TEMPLATES: HousingTemplate[] = [
  {
    id: 'canada-average-apartment',
    name: 'Canadian Average Apartment',
    question: 'Is the average Canadian apartment cheaper to rent or own over 25 years?',
    attribution: BEN_FELIX,
    source: 'Ben Felix (PWL Capital), "Renting vs. Buying a Home: What People Get Wrong". National average apartment price $512,300 and average rent $2,119 (March 2025). About 2% a year in ongoing costs and a 3% opportunity cost on money tied up in the home.',
    data: { ...CANADA_BASE }
  },
  {
    id: 'canada-cash-purchase',
    name: 'Paying Cash for a Home',
    question: 'If I could pay cash, am I better off buying outright or renting and investing the money?',
    attribution: BEN_FELIX,
    source: 'Ben Felix (PWL Capital). A paid-off home carries a large opportunity cost: in his example the renter who invested the full purchase price ended $2.2M ahead over 50 years.',
    data: { ...CANADA_BASE, downpaymentPercent: 100, downpaymentAmount: 512300 }
  },
  {
    id: 'canada-taxable-investor',
    name: 'Renter Investing in a Taxable Account',
    question: 'What if my RRSP and TFSA are full and my rent savings go into a taxable account?',
    attribution: BEN_FELIX,
    source: 'Ben Felix (PWL Capital). Taxable investing lowers the rent that makes renting and owning comparable; in his example $2,119 drops to about $1,700. We land a little higher because we tax the gain once when the fund is cashed out, the same way the owner is taxed on their sale, rather than shaving the return every year.',
    data: { ...CANADA_BASE, investmentTaxRate: 20 } // 50% inclusion × the 40% marginal rate in CANADA_BASE
  },
  {
    id: 'canada-toronto-first-time-condo',
    name: 'First-Time Buyer: Toronto Condo',
    question: 'A $700K condo with $100K down and CMHC insurance, held 10 years, versus renting at $2,800.',
    attribution: "Larry's Help",
    source: "Larry's Help scenario. Toronto property tax about 0.6%, condo fees $450 a month, 25-year amortization, 14% down so CMHC applies.",
    data: {
      ...CANADA_BASE,
      ownershipYears: 10,
      houseCost: 700000,
      closingCostsPercent: 3,
      useDownpaymentPercent: false,
      downpaymentAmount: 100000,
      downpaymentPercent: 14.3,
      interestRate: 5,
      propertyTaxPercent: 0.6,
      maintenancePercent: 0.5,
      condoFees: 450,
      sellingCostsPercent: 6,
      monthlyRent: 2800
    }
  },
  {
    id: 'canada-own-past-payoff',
    name: 'Staying 30 Years, Mortgage Paid at 25',
    question: 'Once the mortgage is gone, does a paid-off home really cost less than renting?',
    attribution: BEN_FELIX,
    source: "Ben Felix (PWL Capital). After payoff the owner's cash costs drop, but the equity tied up in the home still carries an opportunity cost.",
    data: { ...CANADA_BASE, ownershipYears: 30, mortgageYears: 25 }
  },
  {
    id: 'canada-cottage-short-term-rental',
    name: 'Cottage Bought as a Short-Term Rental',
    question: 'A $499K lakeside cottage with two buildings, bought to rent out. What rent breaks even?',
    attribution: "Larry's Help",
    source: "Larry's Help scenario from a real Kawartha Lakes listing (Sept 2026): $499,000, 1.25 acres, two detached buildings, licensed short-term rental for 8 guests, $2,492 a year in property tax. 20% down, 25-year mortgage at 4.5%, 1.5% maintenance for a rural property on a well. Set Property Management Fee and Vacancy Rate to 0 to read the answer as the net rent you must clear after bookings, cleaning and platform fees.",
    data: {
      ...CANADA_BASE,
      analysisMode: 'landlord-breakeven',
      isLandlord: true,
      taxesPercent: 20, // Landlord mode applies capital gains tax on sale: 50% inclusion × the 40% marginal rate, same as the mode button sets
      ownershipYears: 10,
      houseCost: 499000,
      closingCostsPercent: 2.2,
      useDownpaymentPercent: false,
      downpaymentAmount: 99800,
      downpaymentPercent: 20,
      interestRate: 4.5,
      propertyTaxPercent: 0.5,
      maintenancePercent: 1.5,
      sellingCostsPercent: 7,
      annualHomeInsurance: 1200,
      monthlyRent: 2500,
      propertyManagementFeePercent: 8,
      vacancyRatePercent: 5
    }
  },
  {
    id: 'us-first-time-pmi',
    name: 'US First-Time Buyer with PMI',
    question: 'A $400K home with 10% down and a 30-year fixed mortgage at 6.5%, held 10 years, versus renting at $2,200.',
    attribution: "Larry's Help",
    source: "Larry's Help scenario. PMI at 0.8% a year until the loan reaches 80% of the home value, property tax 1.1%.",
    data: { ...US_BASE }
  },
  {
    id: 'us-move-up-twenty-down',
    name: 'US Move-Up Buyer, 20% Down',
    question: 'A $600K home with 20% down and no PMI, held 10 years, versus renting at $3,000.',
    attribution: "Larry's Help",
    source: "Larry's Help scenario. 30-year fixed at 6.5%, property tax 1.1%, 1% maintenance, $1,800 a year insurance.",
    data: {
      ...US_BASE,
      houseCost: 600000,
      downpaymentAmount: 120000,
      downpaymentPercent: 20,
      monthlyRent: 3000,
      annualHomeInsurance: 1800
    }
  }
];

export const getHousingTemplate = (id: string): HousingTemplate | undefined =>
  HOUSING_TEMPLATES.find(template => template.id === id);
